/**
 * Tracks the user's most recent input modality (keyboard vs pointer) and
 * reflects it on the <html> element as `data-focus-method="keyboard" | "mouse"`.
 *
 * This is needed because the CSS `:focus-visible` pseudo-class always matches
 * text inputs (regardless of whether focus came from a mouse click or keyboard),
 * making it impossible to hide the focus ring on mouse-only input focus with
 * pure CSS. With this attribute, CSS can target the actual focus modality:
 *
 *   html[data-focus-method='mouse'] input:focus { outline: none; }
 *   html[data-focus-method='keyboard'] input:focus { outline: 2px solid ...; }
 */
export function initializeFocusMethodTracking(): void {
    if (typeof document === 'undefined') return;

    const setMethod = (method: 'keyboard' | 'mouse') => {
        document.documentElement.setAttribute('data-focus-method', method);
    };

    // Default to keyboard so assistive-tech users see focus rings on first paint.
    setMethod('keyboard');

    const onKeyDown = (e: KeyboardEvent) => {
        // Ignore modifier-only key events and shortcuts so they don't masquerade as nav keys.
        if (e.metaKey || e.altKey || e.ctrlKey) return;
        // Only treat navigation keys as keyboard focus method.
        // Character input (typing into a text field) should NOT flip the modality,
        // otherwise clicking into a field and typing would show the focus ring.
        const key = e.key || '';
        if (key === 'Tab' || key === 'Escape' || key.startsWith('Arrow')) {
            setMethod('keyboard');
        }
    };

    const onPointer = () => setMethod('mouse');

    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('mousedown', onPointer, true);
    document.addEventListener('pointerdown', onPointer, true);
    document.addEventListener('touchstart', onPointer, true);
}
